// frontend/src/features/customFields/api.ts
// AM-009 update: adds listCustomFieldTargetTypes(). Everything else is
// byte-for-byte identical to the real, already-integrated file (verified
// against Drive before writing this).

import { axiosInstance } from "@/api/client";
import { AxiosError } from "axios";
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInput,
  CustomFieldForTargetResponse,
} from "./types";

function handleError(error: unknown): never {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail || error.response?.data || error.message;
    const detailStr = typeof detail === "object" ? JSON.stringify(detail) : String(detail);
    throw new Error(`Custom Fields API error (${error.response?.status || 'Unknown'}): ${detailStr}`);
  }
  throw error;
}

export async function listCustomFieldDefinitions(
  targetContentType?: string
): Promise<CustomFieldDefinition[]> {
  try {
    const params = targetContentType ? { target_content_type: targetContentType } : {};
    const response = await axiosInstance.get<{ results: CustomFieldDefinition[] } | CustomFieldDefinition[]>("/api/v1/custom-field-definitions/", { params });
    const data = response.data;
    return Array.isArray(data) ? data : data.results;
  } catch (error) {
    handleError(error);
  }
}

// AM-009 (closes ADR-095 OQ-CF-01): replaces the hardcoded
// KNOWN_TARGET_TYPES list in CustomFieldsManagerPage.tsx — the backend now
// has a real, enforced registry (shared.entity_search) instead of an
// unvalidated assumption.
export async function listCustomFieldTargetTypes(): Promise<string[]> {
  try {
    const response = await axiosInstance.get<{ target_types: string[] }>(
      "/api/v1/custom-field-definitions/target-types/"
    );
    return response.data.target_types;
  } catch (error) {
    handleError(error);
  }
}

export async function createCustomFieldDefinition(
  input: CustomFieldDefinitionInput
): Promise<CustomFieldDefinition> {
  try {
    const response = await axiosInstance.post<CustomFieldDefinition>("/api/v1/custom-field-definitions/", input);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function updateCustomFieldDefinition(
  id: string,
  input: Partial<CustomFieldDefinitionInput>
): Promise<CustomFieldDefinition> {
  try {
    const response = await axiosInstance.patch<CustomFieldDefinition>(`/api/v1/custom-field-definitions/${id}/`, input);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function deactivateCustomFieldDefinition(
  id: string
): Promise<CustomFieldDefinition> {
  try {
    const response = await axiosInstance.post<CustomFieldDefinition>(`/api/v1/custom-field-definitions/${id}/deactivate/`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function activateCustomFieldDefinition(
  id: string
): Promise<CustomFieldDefinition> {
  try {
    const response = await axiosInstance.post<CustomFieldDefinition>(`/api/v1/custom-field-definitions/${id}/activate/`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function getCustomFieldsForTarget(
  targetContentType: string,
  targetObjectId?: string
): Promise<CustomFieldForTargetResponse> {
  try {
    const params: Record<string, string> = { target_content_type: targetContentType };
    if (targetObjectId) {
      params.target_object_id = targetObjectId;
    }
    const response = await axiosInstance.get<CustomFieldForTargetResponse>("/api/v1/custom-field-values/for-target/", {
      params,
    });
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function setCustomFieldValues(
  targetContentType: string,
  targetObjectId: string,
  values: Record<string, unknown>
): Promise<void> {
  try {
    await axiosInstance.post("/api/v1/custom-field-values/set-values/", {
      target_content_type: targetContentType,
      target_object_id: targetObjectId,
      values,
    });
  } catch (error) {
    handleError(error);
  }
}

export async function uploadCustomFieldMedia(file: File): Promise<{ id: string }> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosInstance.post<{ id: string }>(
      "/api/v1/custom-field-media/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}
